// A single horizontal lane for one voice.
//
// Position is driven at 60fps by an SVG transform on the hap `<g>`,
// sourced from a MutableRefObject<number> (current cycle playhead).
// Hap data itself is re-rendered only when the parent's HapDataFrame
// updates (~12fps). Result: motion is buttery, React reconciliation
// runs at the data rate.
//
// Ticks are positioned in *absolute* cycle coordinates (begin × pxPerCycle).
// The group transform translates by (staticNowX − nowRef × pxPerCycle)
// so a hap at begin=N sits under the static now-line exactly when
// nowRef reaches N. Coord magnitude grows unbounded over very long
// sessions — fine for hours, documented.

import { memo, useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { subscribeTick } from '../../strudel/sharedTicker';
import { useVoiceHaps } from '../../strudel/useHaps';
import type { HapRefs, VisualHap } from '../../strudel/useHaps';
import { LevelMeter } from './LevelMeter';
import { colorForVoice } from './palette';

type Props = {
  voice: string;
  refs: HapRefs;
  nowRef: MutableRefObject<number>;
  lookbehind: number;
  lookahead: number;
  /** Per-lane pixel height. Defaults to 44 (the original chassis size).
   *  The slim PerformanceStrip passes 28 to fit more voices in less
   *  vertical real estate. The SVG viewBox scales with this value, so
   *  the rAF transform path is unchanged. */
  height?: number;
};

const DEFAULT_TRACE_HEIGHT = 44;
const VIEW_W = 1000;
// Afterglow window: how long past the now-line a hap continues to flare.
const FLARE_DECAY_CYCLES = 0.35;
// Anticipation window: soft pre-glow as a hap approaches the now-line.
// Intentionally smaller + weaker than the tail so the hit still reads
// as the peak, not a symmetric blur.
const FLARE_LEAD_CYCLES = 0.08;
const FLARE_LEAD_INTENSITY = 0.35;

type HapLayout = {
  hap: VisualHap;
  x: number;
  cy: number;
  tickH: number;
  durPx: number;
  glowR: number;
};

// Memoized on its structural props. The hap stream is pulled via
// useVoiceHaps from the shared ref — that hook only triggers a
// re-render when this voice's hap keys actually change, so the
// memo + hook together skip ~11/12 polls during steady playback.
export const VoiceTrace = memo(VoiceTraceInner);

function VoiceTraceInner({
  voice,
  refs,
  nowRef,
  lookbehind,
  lookahead,
  height: heightProp,
}: Props) {
  const haps = useVoiceHaps(refs, voice);
  const groupRef = useRef<SVGGElement>(null);
  const flareGroupRef = useRef<SVGGElement>(null);

  const TRACE_HEIGHT = heightProp ?? DEFAULT_TRACE_HEIGHT;
  const VIEW_H = TRACE_HEIGHT;
  const slim = TRACE_HEIGHT < 36;

  const windowLen = lookbehind + lookahead;
  const pxPerCycle = VIEW_W / windowLen;
  const staticNowX = lookbehind * pxPerCycle;
  const color = colorForVoice(voice);
  // Capture once per render for cycle-boundary marks — they only
  // refresh when haps change (triggering a re-render), not every
  // frame. Using nowRef.current directly at render time is fine
  // because React's commit timing is consistent enough.
  const initialNow = nowRef.current;

  // Precompute the geometry once per data render — ticks and flare
  // halos both need the same x/tickH/etc., and recomputing inside
  // two separate .map() calls would drift if one ever changed.
  const layouts: HapLayout[] = haps.map((hap) => {
    const x = hap.begin * pxPerCycle;
    const gain = clampGain(hap.value.gain);
    const tickH = VIEW_H * (0.25 + gain * 0.55);
    const cy = VIEW_H / 2;
    const durPx = Math.max(2, (hap.duration / windowLen) * VIEW_W);
    const glowR = tickH * 1.15;
    return { hap, x, cy, tickH, durPx, glowR };
  });

  // Animation runs off the shared ticker. Reads nowRef (also
  // ticker-driven) and directly mutates SVG attributes — never touches
  // React state. Each subscribed tick does one transform update plus
  // per-flare opacity / radius calc; for typical 30-hap windows that
  // completes in well under 1ms.
  useEffect(() => {
    return subscribeTick(() => {
      const g = groupRef.current;
      const flareEls = flareGroupRef.current?.children;
      if (!g) return;
      const now = nowRef.current;
      const tx = staticNowX - now * pxPerCycle;
      g.setAttribute('transform', `translate(${tx} 0)`);

      if (flareEls) {
        for (let i = 0; i < flareEls.length; i++) {
          const el = flareEls[i] as SVGCircleElement | undefined;
          if (!el) continue;
          const beginAttr = el.getAttribute('data-begin');
          if (beginAttr === null) continue;
          const begin = Number(beginAttr);
          const dist = now - begin;
          let flare = 0;
          if (dist >= 0 && dist < FLARE_DECAY_CYCLES) {
            flare = 1 - dist / FLARE_DECAY_CYCLES;
          } else if (dist < 0 && dist > -FLARE_LEAD_CYCLES) {
            flare = (1 + dist / FLARE_LEAD_CYCLES) * FLARE_LEAD_INTENSITY;
          }
          if (flare > 0) {
            const baseR = Number(el.getAttribute('data-base-r'));
            el.setAttribute('r', String(baseR * (1 + flare * 0.8)));
            el.style.opacity = String(flare);
          } else {
            el.style.opacity = '0';
          }
        }
      }
    });
  }, [nowRef, pxPerCycle, staticNowX]);

  return (
    <div
      className="flex items-stretch border-b border-zinc-900 last:border-b-0"
      style={{ height: TRACE_HEIGHT }}
    >
      {slim ? (
        // Slim variant — single horizontal row: voice name + inline level
        // meter. Keeps the lane to ~28px so 8 voices fit where 4 used to.
        <div
          className="flex items-center gap-2 pl-2 pr-2 border-r border-zinc-900 bg-zinc-950/70 text-[10px] tracking-[0.16em] uppercase overflow-hidden"
          style={{ color }}
          title={voice}
        >
          <span className="font-display whitespace-nowrap overflow-hidden text-ellipsis w-[64px] sm:w-[80px] text-right leading-none">
            {voice}
          </span>
          <LevelMeter voice={voice} refs={refs} nowRef={nowRef} width={28} height={3} />
        </div>
      ) : (
        <div
          className="flex flex-col items-end justify-center gap-1 pr-3 border-r border-zinc-900 bg-zinc-950/60 text-[10px] sm:text-[11px] tracking-[0.18em] uppercase overflow-hidden"
          style={{ color }}
          title={voice}
        >
          <span className="font-display whitespace-nowrap overflow-hidden text-ellipsis w-[72px] sm:w-[96px] text-right">
            {voice}
          </span>
          <LevelMeter voice={voice} refs={refs} nowRef={nowRef} width={56} height={4} />
        </div>
      )}

      <div className="relative flex-1 overflow-hidden">
        {/* Soft horizontal gradient — future bright, past fades. */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(to right, rgba(9,9,11,0.9) 0%, rgba(9,9,11,0.4) 30%, rgba(9,9,11,0.2) 60%, rgba(24,24,27,0.4) 100%)',
          }}
        />

        <svg
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          preserveAspectRatio="none"
          className="absolute inset-0 w-full h-full"
          aria-hidden="true"
        >
          {/* Scrolling content — position driven by rAF transform. */}
          <g ref={groupRef}>
            {/* Cycle boundary guides at integer cycles around the current now. */}
            {cycleMarksAround(initialNow, lookbehind, lookahead).map((c) => {
              const x = c * pxPerCycle;
              return (
                <line
                  key={`c${c}`}
                  x1={x}
                  y1={0}
                  x2={x}
                  y2={VIEW_H}
                  stroke="#27272a"
                  strokeWidth={0.5}
                  strokeDasharray="2 3"
                />
              );
            })}

            {/* Flare halos — separate layer so the rAF loop can update
                opacity without re-rendering the whole tick set. */}
            <g ref={flareGroupRef} filter="url(#voice-glow)">
              {layouts.map(({ hap, x, cy, glowR }) => (
                <circle
                  key={`f${hap.key}`}
                  data-begin={hap.begin.toFixed(6)}
                  data-base-r={glowR}
                  cx={x}
                  cy={cy}
                  r={glowR}
                  fill={color}
                  style={{ opacity: 0 }}
                />
              ))}
            </g>

            {/* Ticks themselves — their own opacity/base-height doesn't
                need to animate every frame; re-renders on data updates. */}
            {layouts.map(({ hap, x, cy, tickH, durPx }) => (
              <rect
                key={hap.key}
                x={x - durPx / 2}
                y={cy - tickH / 2}
                width={durPx}
                height={tickH}
                fill={color}
                opacity={0.85}
                rx={0.5}
              />
            ))}
          </g>

          {/* Now line (fixed in screen space). */}
          <rect
            x={staticNowX - 6}
            y={0}
            width={12}
            height={VIEW_H}
            fill="url(#nowGlow)"
          />
          <line
            x1={staticNowX}
            y1={0}
            x2={staticNowX}
            y2={VIEW_H}
            stroke="#fde047"
            strokeWidth={1}
            opacity={0.9}
          />
        </svg>
      </div>
    </div>
  );
}

function cycleMarksAround(now: number, behind: number, ahead: number): number[] {
  // Grab an extra cycle of margin either side so marks don't pop in/out
  // as `now` advances between data re-renders.
  const start = Math.floor(now - behind - 1);
  const end = Math.ceil(now + ahead + 1);
  const out: number[] = [];
  for (let c = start; c <= end; c++) out.push(c);
  return out;
}

function clampGain(v: unknown): number {
  const n = typeof v === 'number' && Number.isFinite(v) ? v : 0.8;
  return Math.min(1, Math.max(0.1, n));
}
